// pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  logoutUser, 
  getMyBookings, 
  cancelBooking, 
  rateBooking, 
  completeService, 
  getBookingStats,
  updateUserProfile 
} from '../services/api';
import { 
  Calendar, 
  Settings, 
  LogOut, 
  User, 
  Home,
  Clock,
  CheckCircle,
  XCircle,
  Star,
  MapPin,
  Phone,
  ArrowLeft,
  TrendingUp,
  Filter,
  Search,
  Plus,
  Key,
  AlertCircle,
  Zap,
  Droplets,
  Wind,
  Tv,
  Refrigerator,
  Microwave,
  Battery,
  Fan,
  Thermometer,
  Drill,
  Scissors,
  Bath,
  Menu
} from 'lucide-react';
import '../styles/Dashboard.css';

const Dashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [bookings, setBookings] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ratingBookingId, setRatingBookingId] = useState(null);
  const [ratingData, setRatingData] = useState({ rating: 0, review: '' });
  const [serviceCompleting, setServiceCompleting] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    inProgress: 0,
    cancelled: 0,
    totalSpent: 0,
    avgRating: 0
  });
  const [profileData, setProfileData] = useState({
    displayName: user?.displayName || '',
    phoneNumber: user?.phoneNumber || '',
    photoURL: user?.photoURL || ''
  });

  // Fetch bookings and stats
  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        setError('');
        
        // Fetch bookings
        const userBookings = await getMyBookings();
        setBookings(userBookings);
        
        // Fetch stats
        const statsData = await getBookingStats();
        setStats({
          total: statsData.total || 0,
          completed: statsData.completed || 0,
          pending: statsData.pending || 0,
          inProgress: statsData.inProgress || 0,
          cancelled: statsData.cancelled || 0,
          totalSpent: statsData.totalSpent || 0,
          avgRating: statsData.averageRating || 0
        });
        
        // Generate notifications
        const newNotifications = [];
        userBookings.forEach(booking => {
          if (booking.status === 'confirmed') {
            newNotifications.push({
              id: `notif-${booking.id}`,
              title: 'Service Confirmed',
              message: `Your ${booking.service} service has been confirmed`,
              time: 'Recently',
              type: 'success',
              read: false
            });
          } else if (booking.status === 'pending') {
            newNotifications.push({
              id: `notif-payment-${booking.id}`,
              title: 'Booking Pending',
              message: `Booking pending for ${booking.service} service`,
              time: 'Recently',
              type: 'warning',
              read: false
            });
          } else if (booking.status === 'in-progress') {
            newNotifications.push({
              id: `notif-progress-${booking.id}`,
              title: 'Service In Progress',
              message: `Your ${booking.service} service has started`,
              time: 'Recently',
              type: 'info',
              read: false
            });
          }
        });
        setNotifications(newNotifications);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      onLogout?.();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      onLogout?.();
      navigate('/');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }
    
    try {
      await cancelBooking(bookingId);
      // Update local state
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId 
          ? { ...booking, status: 'cancelled' }
          : booking
      ));
      alert('Booking cancelled successfully');
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking. Please try again.');
    }
  };

  const handleCompleteService = async (bookingId) => {
    if (!window.confirm('Mark this service as completed?')) {
      return;
    }

    try {
      setServiceCompleting(prev => ({ ...prev, [bookingId]: true }));
      await completeService(bookingId);
      
      alert('Service marked as completed!');
      // Update local state
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId 
          ? { 
              ...booking, 
              status: 'completed',
              serviceCompletedAt: new Date().toISOString()
            }
          : booking
      ));
    } catch (error) {
      console.error('Error completing service:', error);
      alert('Failed to complete service. Please try again.');
    } finally {
      setServiceCompleting(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleRateBooking = async (bookingId) => {
    if (ratingData.rating === 0) {
      alert('Please select a rating');
      return;
    }

    try {
      await rateBooking(bookingId, ratingData.rating, ratingData.review);
      // Update local state
      setBookings(prev => prev.map(booking => 
        booking.id === bookingId 
          ? { 
              ...booking, 
              rating: ratingData.rating,
              review: ratingData.review
            }
          : booking
      ));
      setRatingBookingId(null);
      setRatingData({ rating: 0, review: '' });
      alert('Thank you for your feedback!');
    } catch (error) {
      console.error('Error rating booking:', error);
      alert('Failed to submit rating. Please try again.');
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await updateUserProfile(profileData);
      alert('Profile updated successfully');
      // Update user in local storage
      const currentUser = JSON.parse(localStorage.getItem('user'));
      localStorage.setItem('user', JSON.stringify({
        ...currentUser,
        displayName: profileData.displayName,
        phoneNumber: profileData.phoneNumber,
        photoURL: profileData.photoURL
      }));
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle size={16} className="status-confirmed" />;
      case 'pending':
        return <Clock size={16} className="status-pending" />;
      case 'in-progress':
        return <Settings size={16} className="status-inprogress" />;
      case 'completed':
        return <CheckCircle size={16} className="status-completed" />;
      case 'cancelled':
        return <XCircle size={16} className="status-cancelled" />;
      default:
        return <Clock size={16} className="status-pending" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'pending':
        return 'Pending';
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Pending';
    }
  };

  const getServiceIcon = (serviceName) => {
    const iconProps = { size: 24 };
    
    const icons = {
      'House Cleaning': <Home {...iconProps} />,
      'Kitchen Cleaning': <Home {...iconProps} />,
      'Bathroom Cleaning': <Bath {...iconProps} />,
      'Sofa Cleaning': <Home {...iconProps} />,
      'Water Tank Cleaning': <Droplets {...iconProps} />,
      'AC Service': <Wind {...iconProps} />,
      'Split AC Jet Service': <Wind {...iconProps} />,
      'Split AC Dual Service': <Wind {...iconProps} />,
      'Split AC Triple Service': <Wind {...iconProps} />,
      'Split AC Quad Service': <Wind {...iconProps} />,
      'Window AC Jet Service': <Wind {...iconProps} />,
      'Window AC Dual Service': <Wind {...iconProps} />,
      'Window AC Triple Service': <Wind {...iconProps} />,
      'Window AC Quad Service': <Wind {...iconProps} />,
      'AC Repair': <Wind {...iconProps} />,
      'Split AC Installation': <Wind {...iconProps} />,
      'Window AC Installation': <Wind {...iconProps} />,
      'Split AC Install + Service Combo': <Wind {...iconProps} />,
      'Refrigerator Repair': <Refrigerator {...iconProps} />,
      'Washing Machine Repair': <Settings {...iconProps} />,
      'Washing Machine Top Load Service': <Settings {...iconProps} />,
      'Washing Machine Front Load Service': <Settings {...iconProps} />,
      'Washing Machine Semi Automatic Service': <Settings {...iconProps} />,
      'Washing Machine Installation': <Settings {...iconProps} />,
      'Microwave Oven Repair': <Microwave {...iconProps} />,
      'Hob/Stove Repair': <Settings {...iconProps} />,
      '3 Burner Hob Service': <Settings {...iconProps} />,
      '4 Burner Hob Service': <Settings {...iconProps} />,
      '5 Burner Hob Service': <Settings {...iconProps} />,
      'Kitchen Chimney Repair': <Settings {...iconProps} />,
      'Kitchen Chimney Service': <Settings {...iconProps} />,
      'Kitchen Chimney Installation': <Settings {...iconProps} />,
      'Geyser Repair': <Thermometer {...iconProps} />,
      'Geyser Installation': <Thermometer {...iconProps} />,
      'Geyser Service': <Thermometer {...iconProps} />,
      'RO/Water Purifier Repair': <Droplets {...iconProps} />,
      'RO/Water Purifier Installation': <Droplets {...iconProps} />,
      'RO/Water Purifier Service': <Droplets {...iconProps} />,
      'Air Cooler Repair': <Wind {...iconProps} />,
      'Fan Repair': <Fan {...iconProps} />,
      '1 Fan Installation': <Fan {...iconProps} />,
      '2 Fan Installation': <Fan {...iconProps} />,
      '3 Fan Installation': <Fan {...iconProps} />,
      '4 Fan Installation': <Fan {...iconProps} />,
      'LED TV Repair': <Tv {...iconProps} />,
      '32 inch TV Installation': <Tv {...iconProps} />,
      '43 inch TV Installation': <Tv {...iconProps} />,
      '64 inch TV Installation': <Tv {...iconProps} />,
      '75 inch TV Installation': <Tv {...iconProps} />,
      'Inverter Repair': <Battery {...iconProps} />,
      'Electrician Service': <Zap {...iconProps} />,
      'Plumber Service': <Droplets {...iconProps} />,
      'Carpenter Service': <Drill {...iconProps} />,
      'Men Saloon': <Scissors {...iconProps} />,
      'Women Saloon': <Scissors {...iconProps} />
    };
    
    return icons[serviceName] || <Settings {...iconProps} />;
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.service.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || booking.status === filterStatus;
    
    // Filter based on active tab
    let matchesTab = true;
    if (activeTab === 'pending') {
      matchesTab = booking.status === 'pending' || booking.status === 'confirmed';
    } else if (activeTab === 'in-progress') {
      matchesTab = booking.status === 'in-progress';
    } else if (activeTab === 'completed') {
      matchesTab = booking.status === 'completed';
    } else if (activeTab === 'cancelled') {
      matchesTab = booking.status === 'cancelled';
    }
    // For 'bookings' tab and 'overview', show all bookings
    
    return matchesSearch && matchesFilter && matchesTab;
  });

  const upcomingBookings = bookings
    .filter(b => b.status === 'confirmed' || b.status === 'pending')
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
            <div className="logo-icon">
              <Home size={24} />
            </div>
            <div className="logo-text">
              <h1>EaseMyHome</h1>
              <span>Dashboard</span>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <button 
            className="nav-item"
            onClick={() => navigate('/home')}
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>

          <div className="nav-divider"></div>

          <button 
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('overview');
              setSidebarOpen(false);
            }}
          >
            <TrendingUp size={20} />
            <span>Overview</span>
          </button>

          <button 
            className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('bookings');
              setSidebarOpen(false);
            }}
          >
            <Calendar size={20} />
            <span>All Bookings</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('pending');
              setSidebarOpen(false);
            }}
          >
            <Clock size={20} />
            <span>Upcoming</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'in-progress' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('in-progress');
              setSidebarOpen(false);
            }}
          >
            <Settings size={20} />
            <span>In Progress</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'completed' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('completed');
              setSidebarOpen(false);
            }}
          >
            <CheckCircle size={20} />
            <span>Completed</span>
          </button>
          
          <button 
            className={`nav-item ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('cancelled');
              setSidebarOpen(false);
            }}
          >
            <XCircle size={20} />
            <span>Cancelled</span>
          </button>

          <div className="nav-divider"></div>

          <button 
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('profile');
              setSidebarOpen(false);
            }}
          >
            <User size={20} />
            <span>Profile</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              <button 
                className="mobile-menu-toggle"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Menu size={24} />
              </button>
              <div className="header-title">
                <h1>
                  {activeTab === 'overview' && 'Dashboard Overview'}
                  {activeTab === 'bookings' && 'All Bookings'}
                  {activeTab === 'pending' && 'Upcoming Services'}
                  {activeTab === 'in-progress' && 'Services In Progress'}
                  {activeTab === 'completed' && 'Completed Services'}
                  {activeTab === 'cancelled' && 'Cancelled Services'}
                  {activeTab === 'profile' && 'Profile Settings'}
                </h1>
                <p>
                  {activeTab === 'overview' && 'Your service activity and quick stats'}
                  {activeTab === 'bookings' && 'Manage your service bookings and track their status'}
                  {activeTab === 'pending' && 'View and manage your upcoming service appointments'}
                  {activeTab === 'in-progress' && 'Track services currently being performed'}
                  {activeTab === 'completed' && 'Review your completed services and rate providers'}
                  {activeTab === 'cancelled' && 'View cancelled bookings and rebook services'}
                  {activeTab === 'profile' && 'Manage your personal information'}
                </p>
              </div>
            </div>
          </div>
        </header>

       

        {/* Overview Content */}
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="overview-grid">
              {/* Quick Actions */}
              <div className="quick-actions-card">
                <div className="card-header">
                  <h3>Quick Actions</h3>
                </div>
                <div className="quick-actions">
                  <button className="quick-action-btn primary" onClick={() => navigate('/services')}>
                    <Plus size={20} />
                    <span>Book New Service</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('bookings')}>
                    <Calendar size={20} />
                    <span>View All Bookings</span>
                  </button>
                  <button className="quick-action-btn" onClick={() => setActiveTab('profile')}>
                    <User size={20} />
                    <span>View Profile</span>
                  </button>
                </div>
              </div>

              {/* Upcoming Services */}
              <div className="upcoming-services-card">
                <div className="card-header">
                  <h3>Upcoming Services</h3>
                </div>
                <div className="upcoming-services-list">
                  {upcomingBookings.length > 0 ? (
                    upcomingBookings.slice(0, 3).map(booking => (
                      <div key={booking.id} className="upcoming-service-item">
                        <div className="service-date">
                          <div className="date-day">{new Date(booking.date).getDate()}</div>
                          <div className="date-month">{new Date(booking.date).toLocaleDateString('en-US', { month: 'short' })}</div>
                        </div>
                        <div className="service-info">
                          <h4>{booking.service}</h4>
                          <p>{booking.timeSlot}</p>
                        </div>
                        <button 
                          className="contact-btn"
                          onClick={() => window.open(`tel:${booking.providerPhone || '+91 9876543210'}`)}
                        >
                          <Phone size={16} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="no-upcoming">
                      <p>No upcoming services</p>
                      <button onClick={() => navigate('/services')} className="book-service-btn">
                        Book a Service
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings List */}
        {(activeTab === 'bookings' || activeTab === 'pending' || activeTab === 'in-progress' || activeTab === 'completed' || activeTab === 'cancelled') && (
          <div className="bookings-section">
            <div className="section-header">
              <div className="section-title">
                <h2>Your Service Bookings</h2>
                <p>Track and manage all your booked services</p>
              </div>
              
              {/* Search and Filter */}
              <div className="bookings-controls">
                <div className="search-box">
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                
                <div className="filter-dropdown">
                  <Filter size={20} />
                  <select 
                    value={filterStatus} 
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="error-alert">
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className="bookings-grid">
              {filteredBookings.length > 0 ? (
                filteredBookings.map(booking => (
                  <div key={booking.id} className="booking-card">
                    <div className="booking-header">
                      <div className="service-icon">
                        {getServiceIcon(booking.service)}
                      </div>
                      <div className="service-info">
                        <h3>{booking.service}</h3>
                        <div className="provider-rating">
                          <span className="provider">Service Provider</span>
                          {booking.rating > 0 && (
                            <div className="rating">
                              <Star size={14} fill="currentColor" />
                              <span>{booking.rating}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`status-badge status-${booking.status}`}>
                        {getStatusIcon(booking.status)}
                        {getStatusText(booking.status)}
                      </div>
                    </div>

                    <div className="booking-details">
                      <div className="detail-item">
                        <Calendar size={16} />
                        <span>{new Date(booking.date).toLocaleDateString('en-IN', { 
                          weekday: 'short', 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })} at {booking.timeSlot}</span>
                      </div>
                      
                      <div className="detail-item">
                        <MapPin size={16} />
                        <span>{booking.address?.street || 'Address not specified'}</span>
                      </div>
                      
                      <div className="detail-item">
                        <Clock size={16} />
                        <span>Booking ID: {booking.id?.slice(-8)}</span>
                      </div>

                      {/* Show OTP for confirmed bookings */}
                      {(booking.status === 'confirmed' || booking.status === 'pending') && booking.otp && !booking.otpUsed && (
                        <div className="otp-section">
                          <div className="otp-header">
                            <Key size={16} />
                            <span>Service OTP</span>
                          </div>
                          <div className="otp-display">
                            <span className="otp-code">{booking.otp}</span>
                            <button 
                              className="copy-otp-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(booking.otp);
                                alert('OTP copied to clipboard!');
                              }}
                            >
                              Copy
                            </button>
                          </div>
                          <p className="otp-info">Share this OTP with the service professional</p>
                        </div>
                      )}

                      {/* Show OTP used status for in-progress */}
                      {booking.otpUsed && booking.status === 'in-progress' && (
                        <div className="otp-used">
                          <CheckCircle size={16} />
                          <span>Service in progress</span>
                          <button 
                            className="complete-service-btn"
                            onClick={() => handleCompleteService(booking.id)}
                            disabled={serviceCompleting[booking.id]}
                          >
                            {serviceCompleting[booking.id] ? 'Completing...' : 'Mark Complete'}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="booking-footer">
                      <div className="booking-meta">
                        <div className="price">
                          <strong>₹{booking.servicePrice || 0}</strong>
                        </div>
                        <div className="payment-status">
                          <span className={`payment-badge payment-${booking.paymentStatus}`}>
                            {booking.paymentStatus}
                          </span>
                        </div>
                      </div>
                      
                      <div className="action-buttons">
                        {booking.status === 'pending' || booking.status === 'confirmed' ? (
                          <>
                            <button 
                              className="btn-secondary" 
                              onClick={() => window.open('tel:+919876543210')}
                            >
                              <Phone size={16} />
                              Support
                            </button>
                            <button 
                              className="btn-cancel" 
                              onClick={() => handleCancelBooking(booking.id)}
                            >
                              <XCircle size={16} />
                              Cancel
                            </button>
                          </>
                        ) : booking.status === 'completed' ? (
                          <>
                            <button 
                              className="btn-primary" 
                              onClick={() => navigate('/services')}
                            >
                              <Plus size={16} />
                              Book Again
                            </button>
                            {!booking.rating && (
                              <button 
                                className="btn-secondary"
                                onClick={() => setRatingBookingId(booking.id)}
                              >
                                <Star size={16} />
                                Rate Service
                              </button>
                            )}
                          </>
                        ) : booking.status === 'cancelled' ? (
                          <>
                            <button 
                              className="btn-primary" 
                              onClick={() => navigate('/services')}
                            >
                              <Plus size={16} />
                              Rebook
                            </button>
                          </>
                        ) : (
                          // In-progress actions
                          <>
                            <button 
                              className="btn-secondary" 
                              onClick={() => window.open('tel:+919876543210')}
                            >
                              <Phone size={16} />
                              Support
                            </button>
                            <button 
                              className="btn-primary"
                              onClick={() => handleCompleteService(booking.id)}
                              disabled={serviceCompleting[booking.id]}
                            >
                              <CheckCircle size={16} />
                              {serviceCompleting[booking.id] ? 'Completing...' : 'Complete'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state">
                  <Calendar size={48} />
                  <h3>No bookings found</h3>
                  <p>
                    {activeTab === 'bookings' && "You haven't made any bookings yet."}
                    {activeTab === 'pending' && "You don't have any upcoming services."}
                    {activeTab === 'in-progress' && "No services in progress."}
                    {activeTab === 'completed' && "No completed services yet."}
                    {activeTab === 'cancelled' && "No cancelled services."}
                  </p>
                  <button onClick={() => navigate('/services')} className="btn-primary">
                    Browse Services
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rating Modal */}
        {ratingBookingId && (
          <div className="rating-modal-overlay">
            <div className="rating-modal">
              <div className="rating-modal-header">
                <h3>Rate Your Service</h3>
                <button onClick={() => setRatingBookingId(null)}>×</button>
              </div>
              <div className="rating-modal-body">
                <div className="rating-stars">
                  {[1, 2, 3, 4, 5].map(star => (
                    <button
                      key={star}
                      className={`star-btn ${ratingData.rating >= star ? 'active' : ''}`}
                      onClick={() => setRatingData(prev => ({ ...prev, rating: star }))}
                    >
                      <Star size={32} fill={ratingData.rating >= star ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
                <div className="rating-input">
                  <label>Your Review (Optional)</label>
                  <textarea
                    value={ratingData.review}
                    onChange={(e) => setRatingData(prev => ({ ...prev, review: e.target.value }))}
                    placeholder="Share your experience with this service..."
                    rows={4}
                  />
                </div>
              </div>
              <div className="rating-modal-footer">
                <button className="btn-secondary" onClick={() => setRatingBookingId(null)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={() => handleRateBooking(ratingBookingId)}
                  disabled={ratingData.rating === 0}
                >
                  Submit Rating
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Section */}
        {activeTab === 'profile' && (
          <div className="profile-section">
            <div className="section-header">
              <h2>Profile Information</h2>
              <p>Manage your account details</p>
            </div>
            <div className="profile-content">
              <div className="profile-card">
                <div className="profile-avatar">
                  <User size={48} />
                </div>
                <div className="profile-info">
                  <h3>{user?.displayName || user?.email?.split('@')[0] || 'User'}</h3>
                  <p>{user?.email}</p>
                  <div className="profile-stats">
                    <div className="stat">
                      <span className="stat-value">{stats.total}</span>
                      <span className="stat-label">Total Bookings</span>
                    </div>
                    <div className="stat">
                      <span className="stat-value">{stats.completed}</span>
                      <span className="stat-label">Completed</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="profile-details">
                <div className="detail-group">
                  <label>Full Name</label>
                  <input 
                    type="text" 
                    value={profileData.displayName}
                    onChange={(e) => setProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                  />
                </div>
                <div className="detail-group">
                  <label>Email Address</label>
                  <input type="email" value={user?.email || ''} readOnly />
                </div>
                <div className="detail-group">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    value={profileData.phoneNumber}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  />
                </div>
                <div className="detail-group">
                  <label>Account Created</label>
                  <input type="text" value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'} readOnly />
                </div>
                
                <button className="btn-primary" onClick={handleUpdateProfile}>
                  Update Profile
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Action Button for Quick Booking */}
        <button 
          className="floating-book-btn"
          onClick={() => navigate('/services')}
          title="Book New Service"
        >
          <Plus size={24} />
        </button>
      </div>
    </div>
  );
};

export default Dashboard;